import { Suspense, useEffect, useState, useRef } from 'react';
import './App.css';
import { ConfigProvider } from 'antd';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getLanguageLocale, locales, defaultLanguage } from './i18n'
import intl from 'react-intl-universal';
import { saveLoginResp } from './slices/loginUser';
import routes from './routes';
import { getLanguage } from './selectors';
import { languageApi, userApi } from './api';
import { changeLanguage } from './slices/language';
import { matchMediaChange } from './slices/matchMedia';
import { navChanged } from './slices/nav';
import { Locale } from './i18n';

const scrollThreshold = 100;

const App: React.FC = () => {
	const dispatch = useDispatch()
	const { language } = useSelector(getLanguage);
	const [locale, setLocale] = useState<Locale>(getLanguageLocale(language));
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	
	const matchMediaChanged = (e: MediaQueryListEvent) => {
		dispatch(matchMediaChange(e.matches ? 'mobile' : 'desktop'));
	}

	const setLocaleByLanguage = (lang: string) => {
		let locale = getLanguageLocale(lang);
		dayjs.locale(locale.dayjs);
		intl.init({
			currentLocale: locale.locale,
			locales,
		});
		setLocale(locale);
	}

	const scrollCalculate = (container: HTMLDivElement) => {
		if (container) {
			const isScrollAtTop = container.scrollTop === 0;
			const isScrollAtBottom = !isScrollAtTop &&
				(container.scrollTop + container.clientHeight + scrollThreshold >= container.scrollHeight);
			
			dispatch(navChanged({
				isScrollAtTop: isScrollAtTop,
				isScrollAtBottom: isScrollAtBottom,
				navWidth: container.clientWidth,
			}));
		}
	}

	const handleScroll = () => { 
		const container = scrollContainerRef.current;
		if (container) {
			scrollCalculate(container);
		}
	}

	const handleResize = () => {
	  const container = scrollContainerRef.current;
		if (container) {
			scrollCalculate(container);
		}
	}

  useEffect(() => {
		languageApi.getLanguage(defaultLanguage).then(res => {
			setLocaleByLanguage(res.data || defaultLanguage)
      dispatch(changeLanguage(res.data || defaultLanguage))
		})
		const mediaQuery = window.matchMedia("(max-width: 900px)");
		dispatch(matchMediaChange(mediaQuery.matches ? 'mobile' : 'desktop'));

		mediaQuery.addEventListener("change", matchMediaChanged);

		const container = scrollContainerRef.current;
		if (container) {
			container.addEventListener('scroll', handleScroll, true);
			scrollCalculate(container);
		}

		window.addEventListener('resize', handleResize);
    
		return () => {
			mediaQuery.removeEventListener("change", matchMediaChanged)
			container?.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleResize);
		}

  // eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	let loginResp = userApi.getLoginRespFromLocalStorage()
	let currentTime = new Date().getTime()
	if (loginResp && loginResp.id && loginResp.accessAfter > currentTime - 1000*60*30) {
	  dispatch(saveLoginResp(loginResp));
	}

	const element = useRoutes(routes);
  return (
		<ConfigProvider locale={locale.antd}>
			<div ref={scrollContainerRef} className='App'>
				<Suspense>{element}</Suspense>
			</div>
    </ConfigProvider>
  );
}

export default App;
